/**
 * Modern, Minimal, Outcome-Focused Landing Page (`/`).
 *
 * Inspired by Zapier & world-class SaaS:
 * - Generous vertical breathing room
 * - Hero composition with large 3D mascot co-pilot and floating interactive badges
 * - Spacious alternating 2-column feature showcases for the 3 key capabilities
 * - Concise, outcome-focused copy for accounting, finance, and ecommerce teams
 * - CTAs routing to `/login`
 */

import { Link } from "@tanstack/react-router";
import { useEffect } from "react";
import { useAuth } from "../auth/AuthContext";
import { TaskJinnLogo } from "../components/TaskJinnLogo";

export function LandingPage() {
  const { session } = useAuth();

  useEffect(() => {
    const handleHashChange = () => {
      if (window.location.hash) {
        const id = window.location.hash.replace("#", "");
        const element = document.getElementById(id);
        if (element) {
          element.scrollIntoView({ behavior: "smooth" });
        }
      }
    };

    if (window.location.hash) {
      const timer = setTimeout(handleHashChange, 100);
      window.addEventListener("hashchange", handleHashChange);
      return () => {
        clearTimeout(timer);
        window.removeEventListener("hashchange", handleHashChange);
      };
    }

    window.addEventListener("hashchange", handleHashChange);
    return () => window.removeEventListener("hashchange", handleHashChange);
  }, []);

  const scrollToSection = (id: string) => (e: React.MouseEvent<HTMLAnchorElement>) => {
    const element = document.getElementById(id);
    if (element) {
      e.preventDefault();
      element.scrollIntoView({ behavior: "smooth" });
      window.history.pushState(null, "", `#${id}`);
    }
  };

  return (
    <main data-testid="landing-page" className="overflow-hidden">
      {/* ─── 1. HERO SECTION ──────────────────────────────────────────────────────── */}
      <section className="landing-hero tj-container">
        <div className="landing-hero-glow" aria-hidden="true" />

        <div className="grid grid-cols-1 lg:grid-cols-12 gap-12 lg:gap-8 items-center relative z-10 text-left pb-16">
          {/* Hero Left: Value Proposition, Headlines, CTAs */}
          <div className="lg:col-span-7 flex flex-col items-start">
            {/* Eyebrow Pill */}
            <div className="inline-flex items-center gap-2 rounded-full border border-orange-200/80 bg-orange-50/90 px-4 py-1.5 text-xs font-semibold text-[var(--accent-terracotta)] mb-6 shadow-xs">
              <span className="w-2 h-2 rounded-full bg-[var(--accent-terracotta)] animate-pulse" />
              <span>Supervised Browser Automation for Sensitive Workflows</span>
            </div>

            {/* Big Bold Headline with Terracotta Accent */}
            <h1 className="landing-hero-headline">
              Automate sensitive web workflows{" "}
              <span className="text-[var(--accent-terracotta)]">
                without sharing your credentials.
              </span>
            </h1>

            {/* Short, Punchy Outcome Subtitle */}
            <p className="landing-hero-subtitle mt-6">
              Built for accounting, finance, and ecommerce teams to run high-security web tasks.
              When 2FA or CAPTCHAs appear, take over in one click and hand control straight back.
            </p>

            {/* Dual CTAs (Both go to /login) */}
            <div className="mt-8 flex flex-wrap items-center gap-4">
              {session ? (
                <>
                  <Link
                    to="/dashboard"
                    className="tj-btn tj-btn-primary px-8 py-4 text-base font-semibold"
                  >
                    Open Dashboard
                    <svg className="w-4 h-4 ml-1" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                      <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2.5} d="M14 5l7 7m0 0l-7 7m7-7H3" />
                    </svg>
                  </Link>
                  <Link to="/account" className="tj-btn tj-btn-secondary px-8 py-4 text-base font-semibold">
                    Account &amp; Devices
                  </Link>
                </>
              ) : (
                <>
                  <Link
                    to="/login"
                    className="tj-btn tj-btn-primary px-9 py-4 text-base font-bold shadow-md hover:shadow-lg transition-all"
                  >
                    Get Started
                    <svg className="w-4 h-4 ml-1 transition-transform group-hover:translate-x-1" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                      <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2.5} d="M14 5l7 7m0 0l-7 7m7-7H3" />
                    </svg>
                  </Link>
                  <Link
                    to="/login"
                    search={{ redirect: "/dashboard" }}
                    className="tj-btn tj-btn-secondary px-9 py-4 text-base font-semibold hover:border-orange-300"
                  >
                    Login
                  </Link>
                </>
              )}
            </div>

            {/* Trust Indicators */}
            <div className="mt-8 flex flex-wrap items-center gap-x-6 gap-y-2 text-xs sm:text-sm text-[var(--text-muted)] font-medium">
              <span className="flex items-center gap-1.5">
                <svg className="w-4 h-4 text-emerald-600 shrink-0" viewBox="0 0 20 20" fill="currentColor" aria-hidden="true">
                  <path fillRule="evenodd" d="M10 18a8 8 0 1 0 0-16 8 8 0 0 0 0 16zm3.857-9.809a.75.75 0 0 0-1.214-.882l-3.483 4.79-1.88-1.88a.75.75 0 1 0-1.06 1.061l2.5 2.5a.75.75 0 0 0 1.137-.089l4-5.5z" clipRule="evenodd" />
                </svg>
                Zero-knowledge encryption
              </span>
              <span className="flex items-center gap-1.5">
                <svg className="w-4 h-4 text-emerald-600 shrink-0" viewBox="0 0 20 20" fill="currentColor" aria-hidden="true">
                  <path fillRule="evenodd" d="M10 18a8 8 0 1 0 0-16 8 8 0 0 0 0 16zm3.857-9.809a.75.75 0 0 0-1.214-.882l-3.483 4.79-1.88-1.88a.75.75 0 1 0-1.06 1.061l2.5 2.5a.75.75 0 0 0 1.137-.089l4-5.5z" clipRule="evenodd" />
                </svg>
                Passwords never touch servers
              </span>
              <span className="flex items-center gap-1.5">
                <svg className="w-4 h-4 text-emerald-600 shrink-0" viewBox="0 0 20 20" fill="currentColor" aria-hidden="true">
                  <path fillRule="evenodd" d="M10 18a8 8 0 1 0 0-16 8 8 0 0 0 0 16zm3.857-9.809a.75.75 0 0 0-1.214-.882l-3.483 4.79-1.88-1.88a.75.75 0 1 0-1.06 1.061l2.5 2.5a.75.75 0 0 0 1.137-.089l4-5.5z" clipRule="evenodd" />
                </svg>
                Instant 1-click 2FA handback
              </span>
            </div>
          </div>

          {/* Hero Right: Large Mascot Hero Composition */}
          <div className="lg:col-span-5 landing-hero-visual">
            <div className="relative w-full max-w-[440px] flex items-center justify-center pb-12">
              {/* Floating Top-Right Badge: Zero Knowledge Vault */}
              <div className="landing-floating-badge -top-2 -right-2 sm:-right-4 flex items-center gap-2.5">
                <div className="w-8 h-8 rounded-lg bg-orange-100 text-[var(--accent-terracotta)] flex items-center justify-center text-sm font-bold">
                  🛡️
                </div>
                <div>
                  <div className="text-[11px] font-bold text-stone-900 leading-none">Zero-Knowledge Vault</div>
                  <div className="text-[10px] text-stone-500 mt-1">Client-side Passkey sealed</div>
                </div>
              </div>

              {/* The 3D Robot Genie Mascot */}
              <img
                src="/mascot.png"
                alt="TaskJinn Robot Genie Mascot"
                className="landing-hero-mascot tj-animate-float"
              />

              {/* Floating Bottom Card: 2FA Takeover Simulation with clean borders & generous buffers */}
              <div className="landing-floating-badge bottom-0 left-0 sm:left-2 border-orange-200/90 shadow-xl min-w-[330px] sm:min-w-[360px]">
                <div className="flex items-center justify-between gap-4 pb-2.5 border-b border-orange-100/70">
                  <span className="flex items-center gap-2 text-xs font-bold text-[var(--accent-terracotta)] whitespace-nowrap">
                    <span className="w-2 h-2 rounded-full bg-[var(--accent-terracotta)] animate-ping" />
                    2FA Prompt Detected
                  </span>
                  <span className="text-[10px] bg-orange-50 text-orange-800 font-bold px-2 py-0.5 rounded border border-orange-200 whitespace-nowrap">
                    5s Handback
                  </span>
                </div>
                <div className="mt-3.5 flex items-center justify-between gap-4">
                  <span className="landing-takeover-btn">
                    Take Over &amp; Approve
                  </span>
                  <span className="text-xs text-stone-500 font-medium whitespace-nowrap pr-1">
                    ⚡ Auto-resumes
                  </span>
                </div>
              </div>
            </div>
          </div>
        </div>

        {/* ─── Metric Strip Beneath Hero ─── */}
        <div className="mt-12 pt-12 border-t border-[var(--border-warm)]">
          <div className="grid grid-cols-1 sm:grid-cols-3 gap-8 text-center">
            <div>
              <div className="text-3xl sm:text-4xl font-extrabold text-[var(--accent-terracotta)] tracking-tight">
                20+ Hours
              </div>
              <p className="text-xs sm:text-sm text-stone-600 mt-1.5 font-medium">
                Saved per team member every month
              </p>
            </div>

            <div>
              <div className="text-3xl sm:text-4xl font-extrabold text-[var(--accent-blue)] tracking-tight">
                Zero-Knowledge
              </div>
              <p className="text-xs sm:text-sm text-stone-600 mt-1.5 font-medium">
                Passwords never touch our servers
              </p>
            </div>

            <div>
              <div className="text-3xl sm:text-4xl font-extrabold text-emerald-600 tracking-tight">
                5 Seconds
              </div>
              <p className="text-xs sm:text-sm text-stone-600 mt-1.5 font-medium">
                Average 1-click takeover &amp; handback
              </p>
            </div>
          </div>
        </div>
      </section>

      {/* ─── 2. THE 3 KEY CAPABILITIES (SPACIOUS ALTERNATING ROWS) ───────────────── */}
      <section id="features" className="landing-section-alt scroll-mt-20">
        <div className="tj-container">
          <div className="landing-header">
            <span className="tj-badge tj-badge-amber mb-3 inline-block">Core Capabilities</span>
            <h2 className="serif-heading text-3xl sm:text-4xl lg:text-5xl font-extrabold text-[var(--text-primary)]">
              Built for websites that refuse ordinary bots
            </h2>
            <p className="mt-4 text-base sm:text-lg text-[var(--text-secondary)]">
              Leave fragile RPA behind. Three core capabilities engineered for high-security portals.
            </p>
          </div>

          <div className="space-y-6">
            {/* ── Feature 1: Seamless 2FA Takeover & Handback ── */}
            <div className="landing-feature-row">
              <div className="text-left">
                <div className="w-12 h-12 rounded-xl bg-blue-50 text-[var(--accent-blue)] flex items-center justify-center mb-6 shadow-xs">
                  <svg className="w-6 h-6" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                    <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M15 15l-2 5L9 9l11 4-5 2zm0 0l5 5M7.188 2.239l.777 2.897M5.136 7.965l-2.898-.777M13.95 4.05l-2.122 2.122m-5.657 5.656l-2.12 2.122" />
                  </svg>
                </div>
                <span className="text-xs font-bold uppercase tracking-wider text-[var(--accent-blue)]">
                  Superhuman Automation
                </span>
                <h3 className="serif-heading text-2xl sm:text-3xl lg:text-4xl font-extrabold text-[var(--text-primary)] mt-3 leading-tight">
                  Seamless takeover &amp; handback for sensitive websites
                </h3>
                <p className="mt-4 text-base text-[var(--text-secondary)] leading-relaxed">
                  When an SMS code, hardware key, or CAPTCHA appears, step into the encrypted live
                  browser in one click. Approve it in 5 seconds and hand control straight back.
                </p>
                <div className="mt-6 flex items-center gap-2 text-sm font-bold text-[var(--accent-blue)]">
                  <span>⚡ Never get blocked by 2FA again</span>
                </div>
              </div>

              {/* Visual Card 1: Browser 2FA Window Mockup */}
              <div className="landing-preview-card text-left">
                {/* Mockup Top Window Bar */}
                <div className="flex items-center justify-between pb-3.5 border-b border-stone-200 text-xs font-semibold text-stone-600">
                  <div className="flex items-center gap-2">
                    <span className="w-2.5 h-2.5 rounded-full bg-rose-400 inline-block" />
                    <span className="w-2.5 h-2.5 rounded-full bg-amber-400 inline-block" />
                    <span className="w-2.5 h-2.5 rounded-full bg-emerald-400 inline-block" />
                    <span className="ml-2 font-mono text-xs text-stone-600">secure.bank-portal.com/tax-filing</span>
                  </div>
                  <span className="flex items-center gap-1.5 text-[11px] text-emerald-700 bg-emerald-50 px-2 py-0.5 rounded font-medium border border-emerald-200">
                    <span className="w-1.5 h-1.5 rounded-full bg-emerald-500 animate-ping" />
                    Encrypted Stream
                  </span>
                </div>

                {/* Workflow Content */}
                <div className="py-6 space-y-4">
                  {/* Transaction Context */}
                  <div className="bg-stone-50 border border-stone-200/90 rounded-xl p-3.5 flex items-center justify-between text-xs sm:text-sm">
                    <div>
                      <div className="font-bold text-stone-900">Quarterly Payroll Reconciliation</div>
                      <div className="text-xs text-stone-500 mt-0.5">Batch #4829 • Automated Export</div>
                    </div>
                    <span className="font-mono font-bold text-stone-800 bg-white px-2.5 py-1 rounded border border-stone-200 shadow-xs">
                      $42,850.00
                    </span>
                  </div>

                  {/* 2FA Prompt Card */}
                  <div className="bg-blue-50/80 border-2 border-blue-200 rounded-xl p-4.5 text-left">
                    <div className="flex items-center justify-between">
                      <div className="text-xs sm:text-sm font-bold text-blue-900 flex items-center gap-1.5">
                        <svg className="w-4 h-4 text-blue-700" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                          <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M12 18h.01M8 21h8a2 2 0 0 0 2-2V5a2 2 0 0 0-2-2H8a2 2 0 0 0-2 2v14a2 2 0 0 0 2 2z" />
                        </svg>
                        Two-Factor Authentication
                      </div>
                      <span className="text-[10px] bg-white text-blue-800 font-bold px-2 py-0.5 rounded border border-blue-200">
                        1-Click Takeover
                      </span>
                    </div>

                    <p className="text-xs text-blue-800 mt-1.5 font-medium">
                      SMS security verification code received on registered mobile:
                    </p>

                    <div className="mt-3.5 flex items-center gap-3">
                      <span className="bg-white border border-blue-300 font-mono text-lg font-extrabold px-3.5 py-1.5 rounded tracking-widest text-stone-900 shadow-xs">
                        8 4 9 • 1 0 2
                      </span>
                      <span className="text-xs font-bold text-emerald-800 bg-emerald-100 px-3 py-2 rounded flex items-center gap-1.5">
                        ✓ Approved (4.8s)
                      </span>
                    </div>
                  </div>

                  <div className="flex items-center justify-between text-xs text-stone-500 pt-1">
                    <span className="flex items-center gap-1.5">
                      <span className="w-2 h-2 rounded-full bg-emerald-500" />
                      Control returned to TaskJinn instantly
                    </span>
                    <span className="text-emerald-700 font-bold">100% Flow Completion</span>
                  </div>
                </div>
              </div>
            </div>

            {/* ── Feature 2: Military Strength Encryption (Reversed) ── */}
            <div className="landing-feature-row">
              <div className="order-2 lg:order-1 landing-preview-card text-left">
                <div className="flex items-center justify-between pb-3.5 border-b border-stone-200 text-xs font-semibold text-stone-600">
                  <span className="flex items-center gap-2">
                    <span className="w-2.5 h-2.5 rounded-full bg-[var(--accent-terracotta)]" />
                    <span>Hardware Passkey Cryptography</span>
                  </span>
                  <span className="text-[10px] bg-orange-100 text-orange-800 px-2 py-0.5 rounded font-mono font-bold">
                    Zero-Knowledge
                  </span>
                </div>

                <div className="py-6 space-y-4">
                  {/* Cryptographic Architecture Flow */}
                  <div className="grid grid-cols-3 gap-3.5 text-center">
                    <div className="bg-orange-50/90 border border-orange-200 rounded-xl p-4 shadow-xs">
                      <div className="text-3xl">🔑</div>
                      <div className="text-xs sm:text-sm font-bold text-stone-900 mt-2">Your Device</div>
                      <div className="text-[10px] text-stone-500 mt-0.5 font-mono">WebAuthn PRF</div>
                    </div>
                    <div className="flex flex-col items-center justify-center">
                      <span className="text-xs font-mono font-bold text-[var(--accent-terracotta)]">HKDF-SHA256</span>
                      <div className="w-full border-t-2 border-dashed border-orange-300 my-2" />
                      <span className="text-[11px] text-stone-400 font-medium">Encrypted Tunnel</span>
                    </div>
                    <div className="bg-stone-50 border border-stone-200 rounded-xl p-4 shadow-xs">
                      <div className="text-3xl">🛡️</div>
                      <div className="text-xs sm:text-sm font-bold text-stone-900 mt-2">TaskJinn Cloud</div>
                      <div className="text-[10px] text-emerald-700 font-bold mt-0.5">Zero Access</div>
                    </div>
                  </div>

                  <div className="bg-stone-50 p-4 rounded-xl border border-stone-200/90 text-xs text-stone-700 space-y-1.5">
                    <div className="font-bold text-stone-900 flex items-center gap-1.5 text-xs sm:text-sm">
                      <span>🔒 Mathematically Sealed Against Breaches</span>
                    </div>
                    <p className="text-xs text-stone-500 leading-relaxed">
                      Passwords never leave your machine in plaintext. Even under a total cloud server breach,
                      our engineers cannot read or decrypt your credentials.
                    </p>
                  </div>
                </div>
              </div>

              <div className="order-1 lg:order-2 text-left">
                <div className="w-12 h-12 rounded-xl bg-orange-50 text-[var(--accent-terracotta)] flex items-center justify-center mb-6 shadow-xs">
                  <svg className="w-6 h-6" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                    <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M12 15v2m-6 4h12a2 2 0 0 0 2-2v-6a2 2 0 0 0-2-2H6a2 2 0 0 0-2 2v6a2 2 0 0 0 2 2zm10-10V7a4 4 0 0 0-8 0v4h8z" />
                  </svg>
                </div>
                <span className="text-xs font-bold uppercase tracking-wider text-[var(--accent-terracotta)]">
                  Zero Credential Exposure
                </span>
                <h3 className="serif-heading text-2xl sm:text-3xl lg:text-4xl font-extrabold text-[var(--text-primary)] mt-3 leading-tight">
                  Military strength encryption, we never have access to your keys or data
                </h3>
                <p className="mt-4 text-base text-[var(--text-secondary)] leading-relaxed">
                  All credentials and session tokens are client-side sealed with hardware Passkeys.
                  Your passwords never leave your machine — we can never read or decrypt your data.
                </p>
                <div className="mt-6 flex items-center gap-2 text-sm font-bold text-[var(--accent-terracotta)]">
                  <span>🛡️ Complete zero-knowledge privacy guarantee</span>
                </div>
              </div>
            </div>

            {/* ── Feature 3: AI Blueprints ── */}
            <div className="landing-feature-row">
              <div className="text-left">
                <div className="w-12 h-12 rounded-xl bg-emerald-50 text-emerald-700 flex items-center justify-center mb-6 shadow-xs">
                  <svg className="w-6 h-6" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                    <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M9.663 17h4.673M12 3v1m6.364 1.636l-.707.707M21 12h-1M4 12H3m3.343-5.657l-.707-.707m2.828 9.9a5 5 0 1 1 7.072 0l-.548.547A3.374 3.374 0 0 0 14 18.469V19a2 2 0 1 1 -4 0v-.531c0-.895-.356-1.754-.988-2.386l-.548-.547z" />
                  </svg>
                </div>
                <span className="text-xs font-bold uppercase tracking-wider text-emerald-700">
                  Adaptive Cognitive Blueprints
                </span>
                <h3 className="serif-heading text-2xl sm:text-3xl lg:text-4xl font-extrabold text-[var(--text-primary)] mt-3 leading-tight">
                  AI powered learning &amp; blueprinting for complex tasks
                </h3>
                <p className="mt-4 text-base text-[var(--text-secondary)] leading-relaxed">
                  Cognitive blueprints navigate complex forms and multi-page tables. If a website
                  changes its layout or design, TaskJinn automatically adapts without breaking.
                </p>
                <div className="mt-6 flex items-center gap-2 text-sm font-bold text-emerald-700">
                  <span>🧠 Resilient, self-healing automation</span>
                </div>
              </div>

              {/* Visual Card 3: AI Self-Healing Engine */}
              <div className="landing-preview-card text-left">
                <div className="flex items-center justify-between pb-3.5 border-b border-stone-200 text-xs font-semibold text-stone-600">
                  <span className="flex items-center gap-2">
                    <span className="w-2.5 h-2.5 rounded-full bg-emerald-500" />
                    <span>Dynamic Portal Adaptation Engine</span>
                  </span>
                  <span className="text-[10px] bg-emerald-100 text-emerald-800 px-2 py-0.5 rounded font-mono font-bold">
                    Self-Healing
                  </span>
                </div>

                <div className="py-6 space-y-3.5">
                  <div className="bg-stone-50 border border-stone-200 rounded-xl p-3.5 text-xs sm:text-sm flex items-center justify-between">
                    <div>
                      <div className="font-semibold text-stone-900">Portal Redesign Detected</div>
                      <div className="text-xs text-stone-500">&ldquo;Download Returns&rdquo; button moved to header menu</div>
                    </div>
                    <span className="text-amber-800 font-bold bg-amber-100 px-2.5 py-1 rounded text-xs">
                      DOM Changed
                    </span>
                  </div>

                  <div className="bg-emerald-50/80 border border-emerald-200 rounded-xl p-3.5 text-xs sm:text-sm flex items-center justify-between">
                    <div>
                      <div className="font-bold text-emerald-900">Cognitive Blueprint Auto-Adapted</div>
                      <div className="text-xs text-emerald-700">Semantic target relocated in 120ms</div>
                    </div>
                    <span className="text-emerald-800 font-extrabold bg-emerald-200/70 px-2.5 py-1 rounded text-xs">
                      100% Success
                    </span>
                  </div>

                  <p className="text-xs text-stone-500 pt-1">
                    ✓ Zero maintenance when government or banking portals redesign their UI.
                  </p>
                </div>
              </div>
            </div>
          </div>
        </div>
      </section>

      {/* ─── 3. SOLUTIONS BY PERSONA ─────────────────────────────────────────────── */}
      <section id="solutions" className="landing-section tj-container scroll-mt-20">
        <div className="landing-header">
          <span className="tj-badge tj-badge-amber mb-3 inline-block">Built for Your Team</span>
          <h2 className="serif-heading text-3xl sm:text-4xl lg:text-5xl font-extrabold text-[var(--text-primary)]">
            Outcomes for high-security workflows
          </h2>
          <p className="mt-4 text-base sm:text-lg text-[var(--text-secondary)]">
            Tailored solutions where manual portal logins and fragile RPA waste valuable time.
          </p>
        </div>

        <div className="grid sm:grid-cols-2 lg:grid-cols-4 gap-6 text-left">
          {/* Card 1: Accounting */}
          <div className="landing-solution-card border-t-4 border-t-[var(--accent-terracotta)]">
            <div>
              <div className="w-10 h-10 rounded-xl bg-orange-50 text-[var(--accent-terracotta)] flex items-center justify-center text-lg mb-4">
                📊
              </div>
              <span className="text-xs font-bold uppercase tracking-wider text-[var(--accent-terracotta)]">
                Accounting &amp; CPAs
              </span>
              <h3 className="serif-heading text-lg font-bold text-[var(--text-primary)] mt-2">
                Client portals without credential risk
              </h3>
              <p className="mt-3 text-xs sm:text-sm text-[var(--text-secondary)] leading-relaxed">
                Pull monthly statements and submit tax filings without storing client passwords in
                shared spreadsheets.
              </p>
            </div>
            <div className="mt-6 pt-3 border-t border-stone-100 text-xs font-semibold text-[var(--accent-terracotta)]">
              Outcome: Zero client password liability
            </div>
          </div>

          {/* Card 2: Finance */}
          <div className="landing-solution-card border-t-4 border-t-[var(--accent-blue)]">
            <div>
              <div className="w-10 h-10 rounded-xl bg-blue-50 text-[var(--accent-blue)] flex items-center justify-center text-lg mb-4">
                💼
              </div>
              <span className="text-xs font-bold uppercase tracking-wider text-[var(--accent-blue)]">
                Finance Managers
              </span>
              <h3 className="serif-heading text-lg font-bold text-[var(--text-primary)] mt-2">
                Multi-bank aggregation in minutes
              </h3>
              <p className="mt-3 text-xs sm:text-sm text-[var(--text-secondary)] leading-relaxed">
                Automate daily balance downloads and payment gateway reconciliations with instant
                hardware token approvals.
              </p>
            </div>
            <div className="mt-6 pt-3 border-t border-stone-100 text-xs font-semibold text-[var(--accent-blue)]">
              Outcome: Fast multi-entity close
            </div>
          </div>

          {/* Card 3: Ecommerce */}
          <div className="landing-solution-card border-t-4 border-t-emerald-600">
            <div>
              <div className="w-10 h-10 rounded-xl bg-emerald-50 text-emerald-700 flex items-center justify-center text-lg mb-4">
                🛒
              </div>
              <span className="text-xs font-bold uppercase tracking-wider text-emerald-700">
                Ecommerce Operators
              </span>
              <h3 className="serif-heading text-lg font-bold text-[var(--text-primary)] mt-2">
                Marketplace settlements without bans
              </h3>
              <p className="mt-3 text-xs sm:text-sm text-[var(--text-secondary)] leading-relaxed">
                Extract Amazon Seller Central, Walmart, and supplier inventory reports automatically
                using human-supervised sessions.
              </p>
            </div>
            <div className="mt-6 pt-3 border-t border-stone-100 text-xs font-semibold text-emerald-700">
              Outcome: Safe, unbanned automation
            </div>
          </div>

          {/* Card 4: Government */}
          <div className="landing-solution-card border-t-4 border-t-purple-600">
            <div>
              <div className="w-10 h-10 rounded-xl bg-purple-50 text-purple-700 flex items-center justify-center text-lg mb-4">
                🏛️
              </div>
              <span className="text-xs font-bold uppercase tracking-wider text-purple-700">
                Government &amp; Regulatory
              </span>
              <h3 className="serif-heading text-lg font-bold text-[var(--text-primary)] mt-2">
                Statutory filings with 2FA handback
              </h3>
              <p className="mt-3 text-xs sm:text-sm text-[var(--text-secondary)] leading-relaxed">
                Complete annual company renewals and customs declarations where strict CAPTCHAs break
                traditional RPA bots.
              </p>
            </div>
            <div className="mt-6 pt-3 border-t border-stone-100 text-xs font-semibold text-purple-700">
              Outcome: 100% filing compliance
            </div>
          </div>
        </div>
      </section>

      {/* ─── 4. FINAL CTA BANNER ──────────────────────────────────────────────────── */}
      <section className="tj-container">
        <div className="landing-cta-banner">
          <h2 className="serif-heading text-3xl sm:text-4xl lg:text-5xl font-extrabold text-[var(--text-primary)]">
            Ready to automate your most sensitive workflows?
          </h2>
          <p className="mt-4 text-base sm:text-lg text-[var(--text-secondary)]">
            Start saving hours every month with complete credential privacy and peace of mind.
          </p>

          <div className="mt-10 flex flex-wrap justify-center gap-4">
            <Link
              to="/login"
              className="tj-btn tj-btn-primary px-9 py-4 text-base font-bold shadow-md hover:shadow-lg"
            >
              Get Started
              <svg className="w-4 h-4 ml-1" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M14 5l7 7m0 0l-7 7m7-7H3" />
              </svg>
            </Link>
            <Link
              to="/login"
              search={{ redirect: "/dashboard" }}
              className="tj-btn tj-btn-secondary px-9 py-4 text-base font-semibold"
            >
              Login
            </Link>
          </div>
          <p className="mt-4 text-xs text-[var(--text-muted)]">
            Zero-knowledge architecture · Passkey protected · No credit card required
          </p>
        </div>
      </section>

      {/* ─── 5. MINIMAL FOOTER ────────────────────────────────────────────────────── */}
      <footer className="border-t border-[var(--border-warm)] bg-[var(--bg-card)] py-12 text-xs text-[var(--text-muted)]">
        <div className="tj-container flex flex-wrap items-center justify-between gap-6">
          <div className="flex items-center gap-3">
            <TaskJinnLogo className="w-7 h-7" />
            <span className="font-bold text-base text-[var(--text-primary)]">TaskJinn</span>
            <span>• Human-Supervised Automation</span>
          </div>

          <div className="flex items-center gap-6">
            <a
              href="#features"
              onClick={scrollToSection("features")}
              className="hover:text-[var(--text-primary)] font-medium cursor-pointer"
            >
              Features
            </a>
            <a
              href="#solutions"
              onClick={scrollToSection("solutions")}
              className="hover:text-[var(--text-primary)] font-medium cursor-pointer"
            >
              Solutions
            </a>
            <Link to="/login" className="hover:text-[var(--text-primary)] font-medium">
              Login
            </Link>
          </div>
        </div>

        <div className="tj-container mt-8 flex flex-wrap items-center justify-between gap-4 border-t border-[var(--border-warm)] pt-8">
          <p>© {new Date().getFullYear()} Tensile Labs Limited. All rights reserved.</p>
          <div className="flex flex-wrap items-center gap-3 sm:gap-4 text-xs text-[var(--text-muted)]">
            <Link to="/privacy" className="hover:text-[var(--text-primary)] transition-colors underline-offset-2 hover:underline">
              Privacy Policy
            </Link>
            <span aria-hidden="true">•</span>
            <Link to="/terms" className="hover:text-[var(--text-primary)] transition-colors underline-offset-2 hover:underline">
              Terms of Service
            </Link>
            <span aria-hidden="true" className="hidden sm:inline">•</span>
            <span className="hidden sm:inline">Military strength encryption · Passkey enabled</span>
          </div>
        </div>
      </footer>
    </main>
  );
}
